import { useCallback, useRef, useState } from "react";
import type { ThreadSummary } from "@/entities/thread/types";
import {
  type DesktopHistorySidebarConfig,
  parseDesktopHistorySidebarConfig,
  resolveDesktopHistorySidebarContentState,
  selectDesktopHistoryItems,
  sortDesktopHistoryThreads,
} from "@/features/history/desktopHistorySidebar";
import { historyThreadKey } from "@/features/history/historyIdentity";
import { historyModeLabel } from "@/features/history/historyMode";
import {
  createHistorySidebarMediator,
  type HistorySidebarEffect,
  type HistorySidebarEvent,
  type HistorySidebarEventNotifier,
  isHistorySidebarEvent,
} from "@/features/history/historySidebarMediator";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { useHistoryThreads } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

function load(): DesktopHistorySidebarConfig {
  const storage = resolveSafePersistStorage() as Storage;
  return parseDesktopHistorySidebarConfig(storage.getItem("pc_sidebar_config"));
}

function save(config: DesktopHistorySidebarConfig): void {
  resolveSafePersistStorage().setItem(
    "pc_sidebar_config",
    JSON.stringify(config),
  );
}

export interface HistorySidebarPresentation {
  readonly node: ReturnType<typeof useComponentNode>;
  readonly config: DesktopHistorySidebarConfig;
  readonly historyModeLabel: string;
  readonly historyItemsCount: number;
  readonly historyScopeSlugs: readonly string[];
  readonly threads: readonly (ThreadSummary & {
    readonly boardSlug: string;
    readonly threadId: string;
  })[];
  readonly contentState: ReturnType<
    typeof resolveDesktopHistorySidebarContentState
  >;
  readonly isFetching: boolean;
  readonly isManualRefreshing: boolean;
  readonly onEvent: HistorySidebarEventNotifier;
  readonly toggle: () => void;
}

export function useHistorySidebarPresenter(): HistorySidebarPresentation {
  const historyScope = useHistorySummaryScope();
  const viewedItems = useHistoryStore((state) => state.viewed);
  const postedItems = usePostedHistoryStore((state) => state.posted);
  const clearViewed = useHistoryStore((state) => state.clearHistory);
  const removeViewed = useHistoryStore((state) => state.removeFromHistory);
  const unread = useHistoryStore((state) => state.getUnreadCount);
  const clearPosted = usePostedHistoryStore(
    (state) => state.clearPostedHistory,
  );
  const removePosted = usePostedHistoryStore(
    (state) => state.removeFromPostedHistory,
  );
  const effectRunner = useRef<(effect: HistorySidebarEffect) => void>(
    (): void => undefined,
  );
  const refreshRef = useRef<() => Promise<boolean>>(
    async (): Promise<boolean> => false,
  );
  const [mediator] = useState(() =>
    createHistorySidebarMediator({
      initialConfig: load(),
      runEffect: (effect: HistorySidebarEffect): void => {
        effectRunner.current(effect);
      },
    }),
  );
  useHierarchyLifetime(mediator);

  const config = useMediatorSnapshot(mediator);
  const viewed = selectDesktopHistoryItems(
    selectHistoryItemsForScope(viewedItems, historyScope),
  );
  const posted = selectDesktopHistoryItems(
    selectHistoryItemsForScope(
      selectActivePostedItems(postedItems),
      historyScope,
    ),
  );
  const historyItems = config.historyMode === "posted" ? posted : viewed;
  const query = useHistoryThreads(
    config.historyMode,
    historyItems,
    historyScope,
  );
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    query.refetch,
    query.isFetching,
  );
  refreshRef.current = refreshManually;

  effectRunner.current = (effect: HistorySidebarEffect): void => {
    switch (effect.type) {
      case "persist-config":
        save(effect.config);
        return;
      case "refresh":
        void refreshRef.current();
        return;
      case "remove-item":
        if (effect.mode === "posted") {
          removePosted(effect.boardSlug, effect.threadId);
        } else {
          removeViewed(effect.boardSlug, effect.threadId);
        }
        return;
      case "clear-items":
        if (effect.mode === "posted") {
          clearPosted(effect.boardSlugs);
        } else {
          clearViewed(effect.boardSlugs);
        }
    }
  };

  const node = useComponentNode((event) => {
    if (!isHistorySidebarEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const onEvent = useCallback<HistorySidebarEventNotifier>(
    (event: HistorySidebarEvent): ReturnType<HistorySidebarEventNotifier> =>
      node.dispatch(event).status,
    [node],
  );
  const toggle = useCallback((): void => {
    node.dispatch({ type: "history/sidebar-toggle-requested" });
  }, [node]);

  const historyKeys = new Set(historyItems.map(historyThreadKey));
  const threads = sortDesktopHistoryThreads(
    (query.data ?? []).flatMap((thread) => {
      const boardSlug = thread.b ?? historyScope.slugs[0];
      const item = {
        boardSlug,
        threadId: thread.id,
      };
      return boardSlug !== undefined && historyKeys.has(historyThreadKey(item))
        ? [{ ...thread, boardSlug, threadId: thread.id }]
        : [];
    }),
    historyItems,
    config.sortByNewRes,
    unread,
  );
  const contentState = resolveDesktopHistorySidebarContentState({
    historyCount: historyItems.length,
    isLoading: query.isLoading,
    hasError: query.error !== null,
    threadCount: threads.length,
  });

  return {
    node,
    config,
    historyModeLabel: historyModeLabel(config.historyMode),
    historyItemsCount: historyItems.length,
    historyScopeSlugs: historyScope.slugs,
    threads,
    contentState,
    isFetching: query.isFetching,
    isManualRefreshing,
    onEvent,
    toggle,
  };
}
